import { Component, OnDestroy } from '@angular/core';
import { ERRORS, Constants, ROUTE_NAMES } from '../../models/constants';
import { CoreBaseFormService } from '../../providers/services/base-form.service';
import { CoreDependencyInjectorService } from '../../utilities/services/dependency-injector.service';
import { CoreSessionService } from '../../providers/services/session.service';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, FormControl, AbstractControl } from '@angular/forms';
// import { DynamicFormService } from '../../providers/services/dynamic-form.service';
import { CoreCommonService } from '../../providers/services/common.service';
import { throwError, Subscription } from 'rxjs';
import { ApiSchema, HttpMethods } from '../../models/interfaces';
import { CoreDataShareService } from '../../providers/services/data-share.service';
import { CoreHttpService } from '../../utilities/services/http.service';
// import { environment } from '../../environments/environment';
import { CoreUtilityService } from '../../utilities/services/utility.service';
import { CoreNetworkAwarePreloadStrategy } from '../../utilities/network-aware-preload-strategy.service';
import { SubSink } from 'subsink';
import { Inject } from '@angular/core';

@Component({
  selector: 'app-base-form',
  templateUrl: './base-form.component.html'
})

export class CoreComponent implements OnDestroy {
  fields: Array<any> = [];
  protected coreBaseService: CoreBaseFormService;
  coreSessionService: CoreSessionService;
  protected router: Router;
  protected formBuilder: FormBuilder;
  currentFormGroup: FormGroup;
  protected currentModuleName: string = undefined;
  currentModule: object;
  protected coreCommonService: CoreCommonService;
  coreUtilityService: CoreUtilityService;
  protected coreDataShareService: CoreDataShareService;
  protected coreHttpService: CoreHttpService;
  imagePath: string;
  eventSubscriptionsList$: SubSink;
  currentModuleButtons: object;
  apiEndPoint: ApiSchema;
  coreConnectionService: CoreNetworkAwarePreloadStrategy;
  primaryApplicantDetails: any;

  constructor(@Inject('ENV_CONFIG') public environment: any) {
    // INFO: required below code for testcases - will be used in future
    // this.coreSessionService = new SessionService(null);
    // console.log('in base form in lib', environment);
    this.eventSubscriptionsList$ = new SubSink();
  }

  /**
   * This method will check screte key is matching or not
   */
  private verifySecretKey() {
    const schema = this.coreSessionService.getApplicationSchema();
    return this.coreUtilityService.strictCompare(schema.secretKey, Constants.key);
  }

  /**
   * This method will initialize the base form
   * @param moduleName - module name
   */
  protected async initBaseForm(moduleName: string) {
    try {
      await this.coreInitDependencyInjection();
      if (moduleName !== Constants.modules.IGNORE) {
        this.currentModuleName = moduleName;
      }
      if (!moduleName || moduleName === Constants.modules.IGNORE) {
        return;
      }
      this.currentFormGroup = this.formBuilder.group({});
      if (moduleName !== Constants.modules.ON_BOARDING && !this.verifySecretKey()) {
        return;
      }
      await this.handleFormLoad(moduleName);
      this.initConstants();
    } catch (error) {
      console.log(ERRORS.BASE_FORM_INIT_FAILED, error);
    }
  }

  /**
   * This method will initialize all the required dependency injections
   */
  private coreInitDependencyInjection() {
    try {
      const injector = CoreDependencyInjectorService.getInjector();
      this.coreBaseService = injector.get(CoreBaseFormService);
      this.coreSessionService = injector.get(CoreSessionService);
      this.router = injector.get(Router);
      this.formBuilder = injector.get(FormBuilder);
      this.coreCommonService = injector.get(CoreCommonService);
      this.coreUtilityService = injector.get(CoreUtilityService);
      this.coreDataShareService = injector.get(CoreDataShareService);
      this.coreHttpService = injector.get(CoreHttpService);
      this.coreConnectionService = injector.get(CoreNetworkAwarePreloadStrategy);
      return;
    } catch (error) {
      console.log(ERRORS.DEP_INJECTION_FAILED);
      throwError(error);
    }
  }

  /**
   * This method will initialize the constant values
   */
  private initConstants() {
    this.imagePath = this.coreCommonService.imagePath;
  }

  /**
   * This method will get the schema based on module
   * @param moduleName - current module name
   */
  private async handleFormLoad(moduleName: string) {
    try {
      const currentModuleSchema = await this.getPageSchema(moduleName);
      if (
        this.coreUtilityService.strictCompare(moduleName, Constants.modules.ON_BOARDING) &&
        !this.coreUtilityService.strictCompare(currentModuleSchema.secretKey, Constants.key)
      ) {
        return;
      }
      this.setCurrentModuleToSession(currentModuleSchema);
      const assignSchema = await this.assignSchema(currentModuleSchema);

      // if (currentModuleSchema.hasOwnProperty('controls')) { this.fields = currentModuleSchema.controls; }
    } catch (error) {
      throwError(ERRORS.MODULE_NOT_FOUND, error);
    }
  }

  /**
   * This method will assign schema received from the server
   * @param currentModuleSchema - current module schema
   */
  private assignSchema(currentModuleSchema: object) {
    try {
      const schemaCopyForControls = JSON.parse(JSON.stringify(currentModuleSchema));
      const schemaCopyForButtons = JSON.parse(JSON.stringify(currentModuleSchema));
      const controls: Array<any> = schemaCopyForControls?.controls;
      const ctaButtons = controls.splice(
        controls.findIndex((ctrl) => Constants.BUTTON_CONTROLS_LIST.indexOf(ctrl?.fields[0]?.fieldType) > -1),
        1
      );
      schemaCopyForButtons.controls = ctaButtons || [];
      this.currentModuleButtons = schemaCopyForButtons;
      this.currentModule = schemaCopyForControls ? schemaCopyForControls : {};
      this.fields = schemaCopyForControls?.controls;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * this method will get page schema from the server
   * @param moduleName - module name
   */
  private async getPageSchema(moduleName: string) {
    try {
      // TODO: Change implementation. Make generic fn call.
      let currentPageSchema: any;
      if (moduleName === Constants.modules.ON_BOARDING) {
        currentPageSchema = await this.coreBaseService
          .getOnBoardingPageSchema(moduleName)
          .toPromise()
          .then((val) => val);
      } else {
        currentPageSchema = await this.coreBaseService
          .getModuleSchema(moduleName)
          .toPromise()
          .then((val) => val);
        const filteredSchemaOnAccessibility = this.filterControlsOnDomain(currentPageSchema);
        // const filteredSchema = this.filterControlsOnSelectedValues(filteredSchemaOnAccessibility);
        currentPageSchema = filteredSchemaOnAccessibility;
      }
      return currentPageSchema;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * This method will set the current module schema to session object
   * @param moduleObject - module object
   */
  private setCurrentModuleToSession(moduleObject: any) {
    this.coreSessionService.updateCurrentModule(moduleObject);
    return;
  }

  // /**
  //  * This method will route the user to next page/module
  //  */
  // protected routeNext() {
  //   try {
  //     if (!this.coreConnectionService.hasGoodConnection()) {
  //       return;
  //     }
  //     const allModules: Array<any> = this.getAllModules();
  //     const currentModule = this.coreSessionService.currentModule;
  //     const indexOfCurrentMod = allModules.findIndex((mod) => Number(mod.displayPosition) === Number(currentModule['displayPosition']));
  //     if (indexOfCurrentMod === allModules.length - 1) {
  //       return;
  //     }
  //     this.coreCommonService.routeToPostLoginModule(allModules[indexOfCurrentMod + 1].routeUrl);
  //   } catch (error) {
  //     console.log(ERRORS.ROUTE_NEXT_FAILED);
  //   }
  // }

  // /**
  //  * This method will route the user to previous page/module
  //  */
  // async routePrevious() {
  //   try {
  //     const currentModule = this.coreSessionService.currentModule;
  //     const allModules: Array<any> = this.getAllModules();
  //     const indexOfCurrentMod = allModules.findIndex((mod) => Number(mod.displayPosition) === Number(currentModule['displayPosition']));
  //     if (indexOfCurrentMod <= 0) {
  //       return;
  //     }
  //     await this.coreCommonService.autoSave();
  //     this.coreCommonService.routeToPostLoginModule(allModules[indexOfCurrentMod - 1].routeUrl);
  //   } catch (error) {
  //     console.log('ERROR: ', ERRORS.ROUTE_PREV_FAILED, error);
  //   }
  // }

  /**
   * This method will provide the next module name as per status flow
   */
  protected getNextModuleName() {
    try {
      const allModules: Array<any> = this.getAllModules();
      const currentModule = this.coreSessionService.currentModule;
      const indexOfCurrentMod = allModules.findIndex((mod) => Number(mod.displayPosition) === Number(currentModule['displayPosition']));
      if (indexOfCurrentMod === allModules.length - 1) {
        return;
      }
      return allModules[indexOfCurrentMod + 1].logicalName;
    } catch (error) {
      console.log(ERRORS.NEXT_MODULE_NAME_FETCH_FAILED);
    }
  }

  // /**
  //  * This method will save the form and continue to the next module
  //  * @param url - save api url
  //  */
  // async saveAndContinue(url: any, params?: object) {
  //   try {
  //     if (!this.currentFormGroup || !this.currentFormGroup.valid) {
  //       this.touchAllFields(this.currentFormGroup);
  //       // const invalidControl = document.querySelector('.ng-invalid');
  //       const invalidControl = document.querySelector('.mat-card');
  //       if (invalidControl) {
  //         invalidControl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //       }
  //       return;
  //     }
  //     const response = await this.saveApplicantModule(url, params);
  //     if (response) {
  //       this.routeNext();
  //     }
  //   } catch (error) {
  //     console.log(`Error Status: ${error.status}\nMessage: ${error.message}`);
  //   }
  // }

  // /**
  //  * This method will save the data from current from and add new applicant to the application
  //  * @param url - save api url
  //  */
  // async saveAndAddApplicant(url: string, params?: object) {
  //   try {
  //     if (!this.coreConnectionService.hasGoodConnection()) {
  //       this.coreCommonService.showAlertBar(this.coreCommonService.transalte('login.messages.errorMessages.message1'), true);
  //       return;
  //     }
  //     if (!this.currentFormGroup || !this.currentFormGroup.valid) {
  //       await this.touchAllFields(this.currentFormGroup);
  //       const invalidControl = document.querySelector('.mat-card');
  //       if (invalidControl) {
  //         invalidControl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //       }
  //       return;
  //     }
  //     await this.saveApplicantModule(url, params);
  //     this.coreSessionService.setStorageValue('temp_application_alphId', this.coreSessionService.applicantAplhaId);
  //     this.coreCommonService.setSessionValues(
  //       this.coreSessionService.applicantGroupAplhaId,
  //       null,
  //       this.coreSessionService.currentApplicantVisaCat,
  //       this.coreSessionService.currentApplicantVisaType,
  //       this.coreSessionService.routeId
  //     );
  //     // this.coreCommonService.routeToPostLoginModuleByIndex(0);
  //     // TODO: Check if below code is generic or needs to be parameterized
  //     this.coreCommonService.routeToPostLoginModule(ROUTE_NAMES.ADD_APPLICANT);
  //   } catch (error) {
  //     console.log(`Error Status: ${error.status}\nMessage: ${error.message}`);
  //   }
  // }

  /**
   * This method will call the save api to save the data
   * @param url - save api url
   */
  async saveApplicantModule(url: string, params?: object) {
    try {
      if (!this.coreConnectionService.hasGoodConnection()) {
        this.coreCommonService.showAlertBar(this.coreCommonService.transalte('login.messages.errorMessages.message1'), true);
        return;
      }
      const formDataObject = params ? { ...params, ...this.currentFormGroup.getRawValue() } : this.currentFormGroup.getRawValue();
      // formDataObject['isDraft'] = this.coreBaseService.checkCurrentModuleWithStatus(this.currentModuleName);
      formDataObject['isDraft'] = false; // Need to reset status of application after any modifications(save & continue)
      formDataObject['stage'] = this.getNextModuleName();
      const response = await this.coreBaseService
        .saveModuleForm(url, formDataObject)
        .toPromise()
        .then((val) => val)
        .catch((err) => {
          throw err;
        });
      return response;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will get the api associated with that module schema
   * @param apiName - api name
   * @param schema - schema data
   */
  // protected getApiFromSchema(apiName: string, schema: object): object {
  //   try {
  //     const apis: Array<any> = schema['apis'];
  //     const matchingApi = apis.find(api => api.name === apiName);
  //     return matchingApi;
  //   } catch (error) { throwError(error); }
  // }

  // /**
  //  * getter function for constants
  //  */
  // get appConstants() {
  //   return Constants;
  // }

  /**
   * This method will be called post page load and populate the form
   * @param form - form data
   * @param checkForSavedData - flag to check the page is loaded properly or not
   */
  postPageLoadActivities(form: FormGroup, checkForSavedData?: boolean) {
    try {
      this.setFormGroup(form);
      if (checkForSavedData) {
        this.checkAndPopulateForm(form);
      }
      this.formValueChanges();
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will populate the form
   * @param form - form
   */
  async checkAndPopulateForm(form: FormGroup) {
    try {
      const pageData = await this.getDataFromApiName(this.currentModule, Constants.GET_MODULE_DATA.getModuleData);
      const saveDataToSession = await this.getDataFromApiName(this.currentModule, Constants.GET_MODULE_DATA.saveToSession);

      if (pageData && pageData.length !== 0) {
        const controlNames = Object.keys(form.controls);
        const formValues = controlNames.reduce((o, key) => ({ ...o, [key]: pageData[key] }), {});
        form.patchValue(formValues);
        form.updateValueAndValidity();
      }
      if (saveDataToSession) {
        this.saveRespToSession(this.apiEndPoint?.sessionParams, saveDataToSession);
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will mark the fields of form as touched/dirty
   * @param formGroup - form
   */
  protected touchAllFields(formGroup: FormGroup) {
    for (const controlName of Object.keys(formGroup.controls)) {
      const control = formGroup.get(controlName);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.touchAllFields(control);
      }
    }
    return true;
  }

  // --------------------------------- helper functions ------------------------

  /**
   * This method will fetch the current module name shown on screen
   */
  protected getAllModules(): Array<any> {
    const modules: Array<any> = this.coreSessionService.getAllModulesSchema() || [];
    return modules;
  }

  /**
   * This method will get data base on api name
   * @param currentModule - current module name
   * @param apiName - api name
   */
  async getDataFromApiName(currentModule: object, apiName: string) {
    try {
      this.apiEndPoint = this.coreCommonService.getApiEndPoint(currentModule, apiName);
      const params = this.coreCommonService.extractAndConstructParams(this.apiEndPoint.params, this.currentFormGroup);
      const data = await this.coreBaseService.coreHttpService(this.apiEndPoint['url'], this.apiEndPoint['type'], params);
      return data;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * This method will set the form group
   * @param form - form
   */
  setFormGroup(form: FormGroup) {
    this.currentFormGroup = form;
    return true;
  }

  // // INFO: Do not rearrange params. This function is called from schema
  // /**
  //  * This method will filter the modules based on selected value
  //  * @param actualSchema - original schema
  //  */
  // async filterModulesOnValue(fieldSchema: any, selectedVal: any, form: FormGroup, params: any) {
  //   try {
  //     const fullSchema = this.coreSessionService.getFullApplicationSchema();
  //     if (!fullSchema) {
  //       return;
  //     }
  //     const domainFiltered = await this.coreCommonService.moduleAccessibilityFilter(fullSchema);
  //     const bucketId = this.coreCommonService.getBucketIdFromValue(params, selectedVal);
  //     const visaTypeFiltered = await this.coreCommonService.visaTypeAccessibilityFilter(domainFiltered, bucketId, selectedVal);
  //     this.coreSessionService.saveApplicationSchemaInBrowser(visaTypeFiltered);
  //     return true;
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }

  // TODO: Club this filter with module accessibility filter and filter in one loop / function;
  /**
   * This method will filter the schema based on accessibility
   * @param actualSchema - original schema
   */
  private filterControlsOnDomain(actualSchema: object): object {
    try {
      const schemaCopy = JSON.parse(JSON.stringify(actualSchema));
      const controls: Array<any> = schemaCopy.controls || [];

      const filteredControls = controls.filter((ctrl) => {
        for (const field of ctrl.fields) {
          const accessibility = field.accessibility;
          if (accessibility?.domain?.value === Constants.ALL || accessibility?.domain?.value === this.environment.applicationType) {
            return ctrl;
          }
        }
      });
      schemaCopy.controls = filteredControls;
      return schemaCopy;
    } catch (error) {
      throw new Error(`schema_filter_failed: ${error}`);
    }
  }

  /**
   * This method will check for the form value changes
   */
  formValueChanges() {
    this.eventSubscriptionsList$.sink = this.currentFormGroup.valueChanges.subscribe(() => {
      const formData = this.currentFormGroup.getRawValue();
      this.coreDataShareService.formDataWithModuleName = {
        [this.currentModuleName]: this.currentFormGroup
      };
    });
  }

  // /**
  //  * This method will hide/show add applicant button based on curr app displayed
  //  */
  // hideAddApplicant(): boolean {
  //   try {
  //     return this.coreSessionService.addApplicantFlag;
  //   } catch (error) {
  //     console.log(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
  //     return false;
  //   }
  // }

  // /**
  //  * This method will enable/disable URN field based on conditions
  //  */
  // disableURN() {
  //   try {
  //     let data;
  //     let flag = true;
  //     this.eventSubscriptionsList$.sink = this.coreCommonService.primaryApplicantData$.subscribe((applicant) => {
  //       if (Object.keys(applicant).length) {
  //         flag = this.coreUtilityService.strictCompare(applicant.applicationAlphId, this.coreSessionService.applicantAplhaId);
  //       }
  //       data = !(
  //         this.coreUtilityService.strictCompare(applicant.applicationAlphId, this.coreSessionService.applicantAplhaId) &&
  //         this.coreSessionService.applicationStatus === null
  //       );
  //     });
  //     return data;
  //   } catch (error) {
  //     console.log(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
  //     return false;
  //   }
  // }

  /**
   * This method will hide/show add applicant button based on curr app displayed
   */
  showPrimarySelection(): boolean {
    try {
      let data;
      this.eventSubscriptionsList$.sink = this.coreCommonService.primaryApplicantData$.subscribe((applicant) => {
        if (Object.keys(applicant).length) {
          data = !this.coreUtilityService.strictCompare(applicant.applicationAlphId, this.coreSessionService.applicantAplhaId);
          this.primaryApplicantDetails = applicant;
        }
      });
      return data;
    } catch (error) {
      console.log(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
      return false;
    }
  }
  /**
   * This method will hide/show copy primary appc data checkbox based on config
   * @param moduleName - name of service
   */
  getProductConfigFlags(moduleName): boolean {
    try {
      return JSON.parse(this.coreSessionService.getStorageValue(moduleName));
    } catch (error) {
      console.log(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
      return false;
    }
  }

  // /**
  //  * Save data to session
  //  */
  // saveToSession(fieldSchema: any, selectedVal: any, form: FormGroup, params: any) {
  //   try {
  //     const valueOfParams = this.coreCommonService.extractAndConstructParams(params, this.currentFormGroup);
  //     for (const key of Object.keys(valueOfParams)) {
  //       this.coreSessionService.saveToSession(key, valueOfParams[key]);
  //     }
  //     return true;
  //   } catch (error) {
  //     console.log(params, error);
  //   }
  // }

  // /**
  //  * Save data to local storage
  //  */
  // saveToLocalStorage(fieldSchema: any, selectedVal: any, form: FormGroup, params: any) {
  //   try {
  //     const valueOfParams = this.coreCommonService.extractAndConstructParams(params, this.currentFormGroup);
  //     for (const key of Object.keys(valueOfParams)) {
  //       this.coreSessionService.setStorageValue(key, valueOfParams[key]);
  //     }
  //     return true;
  //   } catch (error) {
  //     console.log(params, error);
  //   }
  // }

  /**
   * Save Response data to session
   */
  saveRespToSession(params: any, responseValue: any) {
    try {
      const valueOfParams = this.coreCommonService.extractAndConstructParams(params, this.currentFormGroup, responseValue);
      for (const key of Object.keys(valueOfParams)) {
        this.coreSessionService.saveToSession(key, valueOfParams[key]);
      }
      return true;
    } catch (error) {
      console.log(params, error);
    }
  }

  /**
   * Scroll user to invalid form fields
   * @param form current form
   */
  scrollToInvalidForm(form) {
    this.touchAllFields(form);
    const invalidControl = document.querySelector('.ng-invalid');
    if (invalidControl) {
      invalidControl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return;
  }

  /**
   * The trackBy function takes the index and the current item as arguments
   * and needs to return the unique identifier for this item
   * @param index - item index
   */
  trackByFn(index: number, item): number {
    if (!item) {
      return null;
    }
    return index;
  }

  /**
   * Unsubscribe Observables and detach event handlers to avoid memory leaks
   */
  ngOnDestroy() {
    this.eventSubscriptionsList$?.unsubscribe();
  }
}
